import React from 'react';
import DroneFleet from '../components/DroneFleet';
import FleetOverviewDashboard from '../components/FleetOverviewDashboard';
import DetailedTelemetryPanel from '../components/DetailedTelemetryPanel';
import FlightControlPanel from '../components/FlightControlPanel';
import AlertsPanel from '../components/AlertsPanel';
import TelemetryLogs from '../components/TelemetryLogs';
import { API_URL, apiFetch } from '../config/api';
import CalibrationWizard from '../components/CalibrationWizard';
import CalibrationPanel from '../components/CalibrationPanel';
import Map3DRenderer from '../components/Map3DRenderer';
import { Icon, Drawer } from '../components/mobile/MobileKit';
import { useNotification } from '../contexts/NotificationContext';

/**
 * Drones Page - Fleet management and control interface
 * Layout: Left sidebar (fleet list) | Main area (dashboard + panels + logs)
 * Mobile: one "Fleet Controller" card; the fleet manager opens in a drawer
 */
function DronesPage({
    telemetry,
    dronesTelemetry,
    drones,
    reorderDrones,
    loadDrones,
    selectedDroneId,
    onDroneSelect,
    isMobileLayout = false
}) {
    const telemetryData = dronesTelemetry || {};
    const activeTelemetry = selectedDroneId && telemetryData[selectedDroneId]
        ? telemetryData[selectedDroneId]
        : telemetry;

    const [showCalibration, setShowCalibration] = React.useState(false);
    const [calibInitialType, setCalibInitialType] = React.useState(null);
    // Desktop-only
    const [logOpen, setLogOpen] = React.useState(false); // right telemetry-log drawer, closed by default
    const [fleetCollapsed, setFleetCollapsed] = React.useState(false); // Fleet Manager sidebar collapse
    // Phone-only
    const [fleetOpen, setFleetOpen] = React.useState(false);
    const [takeoffAlt, setTakeoffAlt] = React.useState(10);
    const [selectedMode, setSelectedMode] = React.useState('GUIDED');
    const [loading, setLoading] = React.useState(false);
    const [statusMessage, setStatusMessage] = React.useState('');
    const { showConfirm } = useNotification();

    const connected = activeTelemetry?.connected || false;
    const armed = activeTelemetry?.armed || false;
    const lat = parseFloat(activeTelemetry?.lat) || 0;
    const lon = parseFloat(activeTelemetry?.lon) || 0;
    const relativeAlt = parseFloat(activeTelemetry?.relative_alt);
    const fallbackAlt = parseFloat(activeTelemetry?.alt);
    const alt = Number.isFinite(relativeAlt)
        ? relativeAlt
        : (Number.isFinite(fallbackAlt) ? fallbackAlt : 0);
    const batteryRemaining = parseFloat(activeTelemetry?.battery_remaining) || 0;
    const batteryVoltage = parseFloat(activeTelemetry?.battery_voltage) || 0;
    const groundspeed = parseFloat(activeTelemetry?.groundspeed) || 0;
    const satellites = parseInt(activeTelemetry?.satellites) || 0;
    const gpsFix = parseInt(activeTelemetry?.gps_fix) || 0;

    const sendCommand = async (endpoint, body = {}) => {
        if (!selectedDroneId) {
            setStatusMessage('❌ Please select a drone first');
            return;
        }
        setLoading(true);
        setStatusMessage('Sending...');
        try {
            const res = await apiFetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ droneId: selectedDroneId, ...body })
            });
            const data = await res.json();
            setStatusMessage(data.success ? `✅ ${data.message || 'Success'}` : `❌ ${data.message || 'Failed'}`);
            return data;
        } catch (error) {
            setStatusMessage(`❌ ${error.message}`);
            return null;
        } finally {
            setLoading(false);
            setTimeout(() => setStatusMessage(''), 3000);
        }
    };

    // LAND is refused while a mission runs; landing anyway is a choice the
    // operator confirms (it used to send force on every tap).
    const landNow = async () => {
        const data = await sendCommand('/land');
        if (data && !data.success && /mission is running/i.test(data.message || '')
            && await showConfirm('A mission is running. Land where the drone is now, leaving its route?')) {
            await sendCommand('/land', { force: true });
        }
    };
    // Cuts the motors: a flying drone falls. One tap was enough before.
    const emergencyStop = async () => {
        if (await showConfirm('EMERGENCY STOP cuts all motors immediately. If the drone is flying it will fall. Continue?')) {
            sendCommand('/force-disarm');
        }
    };

    // ── MOBILE LAYOUT — the Flutter app's "Fleet Controller" card ──
    if (isMobileLayout) {
        const selectedDrone = drones.find((d) => d.id === selectedDroneId);
        const idle = !connected || loading;
        const tel = (label, value) => <div className="m-tel"><span>{label}</span><b>{value}</b></div>;
        return (
            <div className="m-fleet-page">
                <section className="m-glass neon m-card">
                    <header className="m-card-head">
                        <div className="m-card-titles">
                            <h2 className="m-card-title">Fleet Controller</h2>
                            <span className="m-card-sub">
                                <span className={`m-dot ${connected ? 'on' : 'off'}`} />
                                {selectedDrone ? (selectedDrone.name || selectedDrone.id).toUpperCase() : 'NO DRONE SELECTED'}
                            </span>
                        </div>
                        <span className="m-battery-chip">
                            <Icon name="battery_charging_full" size={18} fill />
                            {connected ? `${batteryRemaining.toFixed(0)}%` : '—'}
                        </span>
                        <button type="button" className="m-b m-square-btn" onClick={() => setFleetOpen(true)} aria-label="Fleet manager">
                            <Icon name="format_list_bulleted" size={20} />
                            <span className="m-square-btn-badge">{drones.length}</span>
                        </button>
                    </header>

                    <div className="m-section-title">
                        FLIGHT MODE
                        {connected && activeTelemetry?.mode && <span className="m-section-note">NOW {activeTelemetry.mode}</span>}
                    </div>
                    <div className="m-row">
                        <select className="m-select" value={selectedMode} onChange={(e) => setSelectedMode(e.target.value)} disabled={idle}>
                            {['GUIDED', 'AUTO', 'LOITER', 'ALT_HOLD', 'RTL', 'STABILIZE', 'LAND'].map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <button type="button" className="m-b m-btn primary" onClick={() => sendCommand('/mode', { mode: selectedMode })} disabled={idle}>SET MODE</button>
                    </div>
                    <div className="m-row">
                        <button type="button" className={`m-b m-btn ${armed ? 'ok' : 'warn'}`} onClick={() => sendCommand('/arm')} disabled={armed || idle}>
                            <Icon name="power_settings_new" size={20} />ARM
                        </button>
                        <button type="button" className="m-b m-btn" onClick={() => sendCommand('/disarm')} disabled={!armed || loading}>
                            <Icon name="power_off" size={20} />DISARM
                        </button>
                    </div>

                    <hr className="m-divider" />
                    <div className="m-section-title">TAKEOFF</div>
                    <div className="m-row">
                        <div className="m-stepper">
                            <button type="button" className="m-b" onClick={() => setTakeoffAlt((a) => Math.max(1, a - 1))} aria-label="Lower"><Icon name="remove" size={20} /></button>
                            <input type="number" inputMode="numeric" min="1" max="100" value={takeoffAlt} aria-label="Takeoff altitude (m)"
                                onChange={(e) => setTakeoffAlt(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))} />
                            <button type="button" className="m-b" onClick={() => setTakeoffAlt((a) => Math.min(100, a + 1))} aria-label="Higher"><Icon name="add" size={20} /></button>
                        </div>
                        <button type="button" className="m-b m-btn cyan" onClick={() => sendCommand('/takeoff', { altitude: takeoffAlt })} disabled={!armed || idle}>
                            <Icon name="flight_takeoff" size={20} />TAKEOFF
                        </button>
                    </div>
                    <div className="m-row">
                        <button type="button" className="m-b m-btn" onClick={landNow} disabled={!armed || !connected}>
                            <Icon name="flight_land" size={20} />LAND
                        </button>
                        <button type="button" className="m-b m-btn" onClick={() => sendCommand('/mode', { mode: 'RTL' })} disabled={!armed || !connected}>
                            <Icon name="home" size={20} />RTL
                        </button>
                    </div>

                    {/* Force-disarm: cuts motors regardless of flight state */}
                    <button type="button" className="m-b m-btn m-estop" onClick={emergencyStop} disabled={!connected}>
                        <Icon name="emergency" size={26} />EMERGENCY STOP
                    </button>

                    {statusMessage && (
                        <div className={`m-status ${statusMessage.includes('✅') ? 'ok' : statusMessage.includes('❌') ? 'err' : ''}`}>
                            {statusMessage.replace(/^[✅❌]\s*/u, '')}
                        </div>
                    )}

                    <hr className="m-divider" />
                    <div className="m-section-title">TELEMETRY</div>
                    <div className="m-tel-grid">
                        {tel("Latitude", connected ? `${lat.toFixed(6)}°` : '—')}
                        {tel("Longitude", connected ? `${lon.toFixed(6)}°` : '—')}
                        {tel("Altitude", connected ? `${alt.toFixed(1)} m` : '—')}
                        {tel("Battery", connected ? `${batteryVoltage.toFixed(1)}V · ${batteryRemaining.toFixed(0)}%` : '—')}
                        {tel("Speed", connected ? `${groundspeed.toFixed(1)} m/s` : '—')}
                        {tel("GPS", connected ? `${satellites} Sats (${gpsFix >= 3 ? '3D' : gpsFix === 2 ? '2D' : 'no fix'})` : '—')}
                    </div>

                    <button type="button" className="m-b m-btn outline" onClick={() => { setCalibInitialType(null); setShowCalibration(true); }} disabled={idle}>
                        <Icon name="shield" size={20} />CALIBRATE SENSORS
                    </button>
                </section>

                {/* Fleet manager (select / connect / add incl. Sim-Mock / edit / delete) — same one desktop uses */}
                {fleetOpen && (
                    <Drawer onClose={() => setFleetOpen(false)}>
                        <DroneFleet
                            drones={drones}
                            reorderDrones={reorderDrones}
                            loadDrones={loadDrones}
                            selectedDroneId={selectedDroneId}
                            onDroneSelect={(id) => { onDroneSelect(id); if (id) setFleetOpen(false); }}
                        />
                    </Drawer>
                )}

                {showCalibration && (
                    <CalibrationWizard
                        droneId={selectedDroneId}
                        telemetry={activeTelemetry}
                        initialType={calibInitialType}
                        onClose={() => {
                            setShowCalibration(false);
                            setCalibInitialType(null);
                            if (loadDrones) loadDrones();
                        }}
                    />
                )}
            </div>
        );
    }

    // ── DESKTOP LAYOUT (Stitch style) ──
    return (
        <div style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            background: 'var(--bgPrimary)',
        }}>
            {/* Left Sidebar — Fleet List */}
            <div style={{
                width: fleetCollapsed ? '72px' : '260px',
                height: '100%',
                flexShrink: 0,
                borderRight: '1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent)',
                background: 'var(--surface-container-low, #1b1c1d)',
                position: 'relative',
                transition: 'width 0.25s ease',
            }}>
                <DroneFleet
                    drones={drones}
                    reorderDrones={reorderDrones}
                    loadDrones={loadDrones}
                    selectedDroneId={selectedDroneId}
                    onDroneSelect={onDroneSelect}
                    isCollapsed={fleetCollapsed}
                />

                {/* Collapse toggle — middle of right border */}
                <button
                    onClick={() => setFleetCollapsed(c => !c)}
                    title={fleetCollapsed ? 'Expand fleet panel' : 'Collapse fleet panel'}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translate(50%, -50%)',
                        zIndex: 30,
                        width: '26px',
                        height: '26px',
                        padding: 0,
                        margin: 0,
                        borderRadius: '50%',
                        background: 'var(--bgPrimary)',
                        color: 'var(--accentSecondary)',
                        border: '1px solid color-mix(in srgb, var(--accent) 50%, transparent)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                        {fleetCollapsed ? 'chevron_right' : 'chevron_left'}
                    </span>
                </button>
            </div>

            {/* Main Content */}
            <div style={{
                flex: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minWidth: 0,
                position: 'relative',
            }}>
                {/* Top Status Bar */}
                <FleetOverviewDashboard drones={drones} telemetryData={telemetryData} />

                {/* Dashboard */}
                <div className="custom-scrollbar" style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    minHeight: 0,
                }}>
                    {/* 2-col grid, 2 rows: telemetry | controls  /  feed | alerts */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '24px',
                        alignItems: 'start',
                    }}>
                        {/* Row 1: telemetry | controls */}
                        <DetailedTelemetryPanel telemetry={activeTelemetry} droneId={selectedDroneId} />

                        <FlightControlPanel telemetry={activeTelemetry} droneId={selectedDroneId} />

                        {/* Row 2: live feed (left) side-by-side with alerts (right).
                            Feed = real FPV camera stream when available, else the 3D map
                            onboard (first-person) view following this drone. */}
                        {(() => {
                            const selectedDrone = drones.find((d) => d.id === selectedDroneId);
                            // ponytail: no camera plumbing exists yet — this is the one-line swap
                            // point. When a drone gains a stream, expose drone.streamUrl and
                            // render <video> here instead of the FPV map.
                            const streamUrl = selectedDrone?.streamUrl || activeTelemetry?.streamUrl;
                            return (
                                <div className="panel-border" style={{
                                    background: 'var(--stitch-surface-lowest)',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    aspectRatio: '16 / 9',
                                }}>
                                    {streamUrl ? (
                                        <video
                                            src={streamUrl}
                                            autoPlay muted playsInline
                                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Map3DRenderer
                                            stitchChrome
                                            isActive
                                            cameraMode="first_person"
                                            followDroneId={selectedDroneId}
                                            dronesTelemetry={telemetryData}
                                            drones={drones}
                                            onDroneSelect={onDroneSelect}
                                        />
                                    )}
                                    <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 950 }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            padding: '4px 12px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                                            borderRadius: '9999px', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
                                            fontSize: '10px', fontWeight: 700, color: 'var(--accentSecondary)',
                                            textTransform: 'uppercase', letterSpacing: '0.08em',
                                        }}>
                                            <span style={{ position: 'relative', display: 'inline-flex', width: '8px', height: '8px' }}>
                                                <span className="stitch-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--danger)' }} />
                                                <span style={{ position: 'relative', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--danger)' }} />
                                            </span>
                                            {streamUrl ? 'LIVE FEED' : 'FPV · ONBOARD VIEW'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Alerts — side by side with the feed */}
                        <div className="panel-border" style={{ background: 'var(--stitch-surface-lowest)', borderRadius: '12px', height: '100%', minHeight: '260px', overflow: 'hidden' }}>
                            <AlertsPanel
                                telemetryData={telemetryData}
                                drones={drones}
                                selectedDroneId={selectedDroneId}
                                telemetry={activeTelemetry}
                            />
                        </div>
                    </div>

                    {/* Sensor Calibration — full width */}
                    <div className="panel-border" style={{ background: 'var(--stitch-surface-lowest)', borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '16px 20px', borderBottom: '1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent)', gap: '12px',
                        }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--accentSecondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>tune</span>
                                SENSOR CALIBRATION
                            </h3>
                            <button
                                onClick={() => { setCalibInitialType(null); setShowCalibration(true); }}
                                disabled={!connected}
                                style={{
                                    padding: '10px 18px',
                                    background: 'transparent',
                                    color: !connected ? 'var(--textMuted)' : 'var(--accentSecondary)',
                                    border: `1px solid ${!connected ? 'var(--borderSecondary)' : 'var(--accentSecondary)'}`,
                                    borderRadius: '8px',
                                    cursor: !connected ? 'not-allowed' : 'pointer',
                                    fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                                    display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
                                }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>shield</span>
                                CALIBRATE SENSORS
                            </button>
                        </div>
                        <div style={{ height: '360px', overflow: 'hidden' }}>
                            <CalibrationPanel
                                drones={drones}
                                selectedDroneId={selectedDroneId}
                                telemetry={activeTelemetry}
                                onStartCalibration={(type) => { setCalibInitialType(type); setShowCalibration(true); }}
                            />
                        </div>
                    </div>
                </div>

                {/* Telemetry Log — right slide-out drawer (closed by default) */}
                <button
                    onClick={() => setLogOpen(o => !o)}
                    title={logOpen ? 'Close telemetry log' : 'Open telemetry log'}
                    style={{
                        position: 'absolute',
                        top: '96px',
                        right: logOpen ? '440px' : '0',
                        zIndex: 46,
                        width: 'auto',
                        margin: 0,
                        background: 'var(--accentSecondary)',
                        color: 'var(--stitch-on-primary)',
                        border: 'none',
                        borderRadius: '8px 0 0 8px',
                        padding: '12px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 800,
                        fontSize: '11px',
                        letterSpacing: '0.1em',
                        boxShadow: '-6px 0 16px rgba(0,0,0,0.3)',
                        transition: 'right 0.3s ease',
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{logOpen ? 'chevron_right' : 'terminal'}</span>
                    <span style={{ writingMode: 'vertical-rl' }}>LOG</span>
                </button>
                <div style={{
                    position: 'absolute',
                    top: '80px',
                    bottom: 0,
                    right: 0,
                    width: '440px',
                    maxWidth: '90%',
                    transform: logOpen ? 'translateX(0)' : 'translateX(440px)',
                    transition: 'transform 0.3s ease',
                    zIndex: 45,
                    background: 'var(--bgPrimary)',
                    borderLeft: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)',
                    boxShadow: '-20px 0 40px rgba(0,0,0,0.45)',
                    overflow: 'hidden',
                }}>
                    <TelemetryLogs
                        telemetry={selectedDroneId ? (telemetryData[selectedDroneId] || telemetry) : null}
                        droneId={selectedDroneId}
                    />
                </div>
            </div>

            {/* Calibration Wizard Portal */}
            {showCalibration && (
                <CalibrationWizard
                    droneId={selectedDroneId}
                    telemetry={activeTelemetry}
                    initialType={calibInitialType}
                    onClose={() => {
                        setShowCalibration(false);
                        setCalibInitialType(null);
                        if (loadDrones) loadDrones();
                    }}
                />
            )}
        </div>
    );
}

export default DronesPage;
